// src/store/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { 
  signIn as firebaseSignIn, 
  signOut as firebaseSignOut,
  getUserProfile,
  updateLastLogin 
} from "../services/firebase";

/**
 * Async thunk for user sign in
 */
export const signInUser = createAsyncThunk(
  "auth/signIn",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const firebaseUser = await firebaseSignIn(email, password);
      
      const userProfile = await getUserProfile(firebaseUser.uid);
      
      if (!userProfile) {
        return rejectWithValue("User profile not found. Please contact an administrator.");
      }
      
      await updateLastLogin(firebaseUser.uid);
      
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: userProfile.displayName || firebaseUser.displayName || "",
        role: userProfile.role || "client",
        clientAccess: userProfile.clientAccess || [],
      };
    } catch (error) {
      if (error.code === "auth/invalid-email") {
        return rejectWithValue("Invalid email address.");
      } else if (error.code === "auth/user-not-found") {
        return rejectWithValue("No account found with this email address.");
      } else if (error.code === "auth/wrong-password") {
        return rejectWithValue("Incorrect password.");
      } else if (error.code === "auth/invalid-credential") {
        return rejectWithValue("Invalid email or password.");
      } else if (error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to sign in. Please try again.");
    }
  }
);

/**
 * Async thunk for user sign out
 */
export const signOutUser = createAsyncThunk(
  "auth/signOut",
  async (_, { rejectWithValue }) => {
    try {
      await firebaseSignOut();
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to restore user session on app load
 */
export const restoreSession = createAsyncThunk(
  "auth/restoreSession",
  async (firebaseUser, { rejectWithValue }) => {
    if (!firebaseUser) {
      return null;
    }
    
    try {
      const userProfile = await getUserProfile(firebaseUser.uid);
      
      if (!userProfile) {
        return rejectWithValue("User profile not found.");
      }
      
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: userProfile.displayName || firebaseUser.displayName || "",
        role: userProfile.role || "client",
        clientAccess: userProfile.clientAccess || [],
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.loading = false;
      state.error = null;
    },
    
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Sign In
      .addCase(signInUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signInUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(signInUser.rejected, (state, action) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = action.payload;
      })
      
      // Sign Out
      .addCase(signOutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(signOutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      })
      .addCase(signOutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Restore Session
      .addCase(restoreSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = !!action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setUser, setLoading, setError, clearError, logout } = authSlice.actions;

// ============================================
// SELECTORS
// ============================================

export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectUserRole = (state) => state.auth.user?.role || null;
export const selectIsAdmin = (state) => state.auth.user?.role === "admin";
export const selectClientAccess = (state) => state.auth.user?.clientAccess || [];

export const selectHasClientAccess = (state, clientSlug) => {
  const user = state.auth.user;
  if (!user) return false;
  if (user.role === "admin") return true;
  return user.clientAccess?.includes(clientSlug) || false;
};

export default authSlice.reducer;
